import { createContext, useContext, type ReactNode } from 'react'

export interface PermissionContextValue {
  /** may the current user do this in the current workspace? */
  can: (permission: string) => boolean
  roleLabel: string
}

const PermissionContext = createContext<PermissionContextValue>({ can: () => false, roleLabel: 'Guest' })

export function PermissionProvider({
  value,
  children,
}: {
  value: PermissionContextValue
  children: ReactNode
}) {
  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
}

export function usePermission() {
  return useContext(PermissionContext)
}

export interface CanProps {
  /** one permission, or any of several */
  permission: string | string[]
  children: ReactNode
  /** rendered instead of nothing when the user lacks the permission */
  fallback?: ReactNode
}

/**
 * The permission gate (F1): every "may I?" in the screens goes through here, so the real auth layer later
 * changes the provider, never a screen. Example: <Can permission="workflows.publish"><button>Make live</button></Can>
 */
export function Can({ permission, children, fallback = null }: CanProps) {
  const { can } = usePermission()
  const list = Array.isArray(permission) ? permission : [permission]
  return <>{list.some((p) => can(p)) ? children : fallback}</>
}
